import { ICellRendererParams } from '@ag-grid-community/core';

import { TranslationService } from '../../../services/translation.service';
import { UserDatacenterView } from '../../../models/datacenter-aggrid-view';
import { safe } from '../../../utils/safe';
import { OfferItem } from '../../../../offer/models/offer';
import { formatTextDimension } from '../../../utils/number.format';
import { UNDERSCORE } from '../../../utils/constant';
import {
	CommonColDef,
	DateColDef,
	LinkColDef,
	NumericColDef
} from '../../column-defs';
import { ColumnType } from '../../column-type';
import { DatacenterTextFilterComponent } from '../../filter-type/datacenter-text-filter.component';
import { DatacenterDateFilterComponent } from '../../filter-type/datacenter-date-filter.component';
import { DatacenterNumberFilterComponent } from '../../filter-type/datacenter-number-filter.component';

const linkToOfferCardCellRendererParams = {
	routerLink(params: ICellRendererParams<OfferItem, unknown>) {
		if (!params.data) {
			return undefined;
		}
		const offerDataCenter = params.data;

		return {
			commands: [
				'/offer',
				'card',
				`c${ safe(offerDataCenter.opportunityID) }`
			]
		};
	}
};

export class OfferDatacenterColumnDefinition {
	readonly OFFER_DATA_CENTER_DEFAULT_CONFIGURATION: Map<string, CommonColDef<OfferItem>>;

	constructor(private readonly translationService: TranslationService) {
		this.OFFER_DATA_CENTER_DEFAULT_CONFIGURATION = new Map<string, CommonColDef<OfferItem>>([
			[
				'custOffRef',
				new CommonColDef<OfferItem>({
					headerName: this.translationService.getTR('offers.create.details.customer.offer-name'),
					colId: 'custOffRef',
					field: 'custOffRef',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					minWidth: 160,
					pinned: 'left',
					sortable: true,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'suppOffRef',
				new LinkColDef<OfferItem>({
					headerName: this.translationService.getTR('application.procurement.supplier.rfqNumber'),
					colId: 'suppOffRef',
					field: 'suppOffRef',
					cellRendererParams: linkToOfferCardCellRendererParams,
					filter: DatacenterNumberFilterComponent,
					suppressHeaderMenuButton: true,
					sortable: true,
					columnType: ColumnType.TEXT_COLUMN,
					minWidth: 160,
					pinned: 'left'
				})
			],
			[
				'itemNumber',
				new CommonColDef<OfferItem>({
					headerName: this.translationService.getTR('eoffer.table.item.number'),
					colId: 'itemNumber',
					field: 'itemNumber',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: true,
					sortable: false,
					columnType: ColumnType.TEXT_COLUMN,
				})
			],
			[
				'standSteelGrade',
				new CommonColDef<OfferItem>({
					headerName: this.translationService.getTR('eoffer.table.header.standsteelGrade'),
					colId: 'standSteelGrade',
					field: 'standardSteelGrade',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: true,
					sortable: false,
					columnType: ColumnType.TEXT_COLUMN,
					minWidth: 250
				})
			],
			[
				'dimensions',
				new CommonColDef<OfferItem>({
					headerName: this.translationService.getTR('eoffer.table.header.dim'),
					colId: 'dimensions',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: true,
					sortable: false,
					columnType: ColumnType.TEXT_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return undefined;
						}
						const offerDataCenter = params.data;

						const { productDimension } = offerDataCenter;

						if (!productDimension) {
							return undefined;
						}

						return formatTextDimension(
							productDimension.thickness.value,
							productDimension.width.value,
							productDimension.length
								? productDimension.length.value
								: undefined
						);
					}
				})
			],
			[
				'lastModifiedDate',
				new DateColDef<OfferItem>({
					headerName: this.translationService.getTR('eoffer.table.header.last.update'),
					colId: 'lastModifiedDate',
					field: 'lastModifiedDate',
					minWidth: 120,
					maxWidth: 120,
					width: 120,
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'date' },
					filter: DatacenterDateFilterComponent,
					suppressHeaderMenuButton: true,
					sortable: true,
					columnType: ColumnType.DATE_COLUMN
				})
			],
			[
				'status',
				new CommonColDef<OfferItem>({
					headerName: this.translationService.getTR('eoffer.status'),
					colId: 'status',
					field: 'status',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: true,
					sortable: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'price',
				new CommonColDef<OfferItem>({
					headerName: this.translationService.getTR('eoffer.price'),
					colId: 'price',
					field: 'productPrice',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: true,
					sortable: false,
					columnType: ColumnType.TEXT_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const offerDataCenter = params.data;
						if (!offerDataCenter.productCurrency) {
							return undefined;
						}
						if (offerDataCenter.productPrice === undefined) {
							return UNDERSCORE;
						}
						return offerDataCenter.productPrice;
					},
				})
			],
			[
				'quantity',
				new CommonColDef<OfferItem>({
					headerName: this.translationService.getTR('eoffer.quantity'),
					colId: 'quantity',
					field: 'productQuantity',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: true,
					sortable: false,
					columnType: ColumnType.TEXT_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const offerDataCenter = params.data;
						if (!offerDataCenter.productQuantity) {
							return UNDERSCORE;
						}
						return offerDataCenter.productQuantity;
					}
				})
			],
			[
				'periodStart',
				new CommonColDef<OfferItem>({
					headerName: this.translationService.getTR('eoffer.period.start'),
					colId: 'periodStart',
					field: 'periodStart',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: true,
					sortable: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'periodEnd',
				new CommonColDef<OfferItem>({
					headerName: this.translationService.getTR('eoffer.period.end'),
					colId: 'periodEnd',
					field: 'periodEnd',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: true,
					sortable: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'validityDate',
				new DateColDef<OfferItem>({
					headerName: this.translationService.getTR('eoffer.validityDate'),
					colId: 'validityDate',
					field: 'validityDate',
					minWidth: 120,
					maxWidth: 120,
					width: 120,
					filter: DatacenterDateFilterComponent,
					suppressHeaderMenuButton: true,
					sortable: true,
					columnType: ColumnType.DATE_COLUMN
				})
			],
			[
				'subcategory',
				new CommonColDef<OfferItem>({
					headerName: this.translationService.getTR('datacenter.offer.subcategory'),
					colId: 'subcategory',
					field: 'subCategory',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: true,
					sortable: false,
					minWidth: 150,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'form',
				new CommonColDef<OfferItem>({
					headerName: this.translationService.getTR('eoffer.form'),
					colId: 'form',
					field: 'form',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: true,
					sortable: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'creationDate',
				new DateColDef<OfferItem>({
					headerName: this.translationService.getTR('datacenter-filter.creation-date'),
					colId: 'creationDate',
					field: 'creationDate',
					minWidth: 120,
					maxWidth: 120,
					width: 120,
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'date' },
					filter: DatacenterDateFilterComponent,
					suppressHeaderMenuButton: true,
					sortable: false,
					columnType: ColumnType.DATE_COLUMN
				})
			],
			[
				'customer',
				new CommonColDef<OfferItem>({
					headerName: this.translationService.getTR('eoffer.table.customer'),
					colId: 'customer',
					field: 'customer',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: true,
					sortable: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'shipTo',
				new CommonColDef<OfferItem>({
					headerName: this.translationService.getTR('eoffer.table.shipto'),
					colId: 'shipTo',
					field: 'shipTo',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: true,
					sortable: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'endUser',
				new CommonColDef<OfferItem>({
					headerName: this.translationService.getTR('eoffer.table.enduser'),
					colId: 'endUser',
					field: 'endUser',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: true,
					sortable: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'priceUnit',
				new CommonColDef<OfferItem>({
					headerName: this.translationService.getTR('eoffer.table.price.unit'),
					colId: 'priceUnit',
					field: 'priceUnit',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: true,
					sortable: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'priceValue',
				new NumericColDef<OfferItem>({
					headerName: this.translationService.getTR('eoffer.table.price.value'),
					colId: 'priceValue',
					field: 'priceValue',
					filter: DatacenterNumberFilterComponent,
					suppressHeaderMenuButton: true,
					sortable: false,
					columnType: ColumnType.NUMBER_COLUMN,
					cellRendererParams: { numericFormat: 'amount' },
				})
			],
			[
				'quantityUnit',
				new CommonColDef<OfferItem>({
					headerName: this.translationService.getTR('eoffer.table.quantity.unit'),
					field: 'quantityUnit',
					colId: 'quantityUnit',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: true,
					sortable: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'quantityValue',
				new CommonColDef<OfferItem>({
					headerName: this.translationService.getTR('eoffer.table.quantity.value'),
					colId: 'quantityValue',
					field: 'quantityValue',
					filter: DatacenterNumberFilterComponent,
					suppressHeaderMenuButton: true,
					sortable: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			]
		]);
	}
}

export const DEFAULT_VIEW_OFFER_DATACENTER: UserDatacenterView = {
	columnState: [
		{
			colId: 'custOffRef',
			pinned: 'left'
		},
		{
			colId: 'suppOffRef',
			pinned: 'left'
		},
		{
			colId: 'itemNumber',
			pinned: 'left'
		},
		{
			colId: 'standSteelGrade',
			pinned: 'left'
		},
		{
			colId: 'dimensions',
			pinned: 'left'
		},
		{
			colId: 'lastModifiedDate'
		},
		{
			colId: 'status'
		},
		{
			colId: 'price'
		},
		{
			colId: 'quantity'
		},
		{
			colId: 'periodStart'
		},
		{
			colId: 'periodEnd'
		},
		{
			colId: 'validityDate'
		},
		{
			colId: 'subcategory'
		},
		{
			colId: 'form'
		}
	],
	sortModel: [
		{
			colId: 'lastModifiedDate',
			sort: 'desc'
		}
	],
	filterState: [],
	type: 'offer',
	isDefault: true,
	name: 'Default',
	id: -99
};
